import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Typography, LinearProgress, Alert, Button, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import billingApi from '../services/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { services, productLabelToServiceKey } from '../proxy-config';

function getProductUrl(productId) {
  const key = productLabelToServiceKey[productId];
  return key ? services[key]?.frontend : null;
}

// Bounded activation poll: MAX_POLL_ATTEMPTS × POLL_INTERVAL_MS ≈ 30s. The
// progress bar derives its percentage from the same constants.
const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const sessionId = searchParams.get('session_id');
  const isTrial = searchParams.get('trial') === 'true';

  const [status, setStatus] = useState('loading');
  const [subscription, setSubscription] = useState(null);
  const [pollAttempt, setPollAttempt] = useState(0);

  useEffect(() => {
    if (isTrial) {
      // Trial subscriptions are created before checkout, so just poll for it
      pollForSubscription();
      return;
    }
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollForSubscription();
  }, [sessionId, isTrial]);

  async function pollForSubscription() {
    let attempts = 0;

    while (attempts < MAX_POLL_ATTEMPTS) {
      try {
        const data = await billingApi.get('/api/subscriptions');
        const latest = data.subscriptions?.[0];
        if (latest && ['active', 'trialing'].includes(latest.status)) {
          setSubscription(latest);
          setStatus('success');
          // Refresh JWT so plans claim is updated
          if (refreshSession) await refreshSession();
          return;
        }
      } catch {
        // Keep trying
      }
      attempts++;
      setPollAttempt(attempts);
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    setStatus('success');
    if (refreshSession) await refreshSession();
  }

  if (status === 'loading') {
    // Known-duration operation (bounded 15×2s activation poll) → determinate
    // gold progress bar per the brand Loading & Busy-State Standard.
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6">Confirming your subscription...</Typography>
        <Typography color="text.secondary">This may take a moment.</Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min((pollAttempt / MAX_POLL_ATTEMPTS) * 100, 100)}
          color="secondary"
          aria-label="Confirming your subscription"
          sx={(theme) => ({
            mt: 3,
            height: 6,
            borderRadius: '2px',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : theme.palette.divider,
          })}
        />
      </Container>
    );
  }

  if (status === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">Something went wrong. Please check your subscriptions.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/subscriptions')}>View Subscriptions</Button>
      </Container>
    );
  }

  const productUrl = subscription?.productId ? getProductUrl(subscription.productId) : null;

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
      <Typography variant="h4" sx={{ mt: 2 }}>
        {subscription?.status === 'trialing' ? 'Trial Started!' : 'Subscription Active!'}
      </Typography>
      {subscription && (
        <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
          {subscription.product_name} — {subscription.plan_name}
        </Typography>
      )}
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        {subscription?.status === 'trialing'
          ? 'Your free trial is active. Enjoy full access — you won\'t be charged until your trial ends.'
          : 'Your subscription is now active. You can manage it from your subscriptions page.'}
      </Typography>
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        {productUrl && (
          <Button variant="contained" href={productUrl}>
            Go to {subscription.product_name}
          </Button>
        )}
        <Button variant={productUrl ? 'outlined' : 'contained'} onClick={() => navigate('/subscriptions')}>
          View Subscriptions
        </Button>
      </Box>
    </Container>
  );
}
